import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { HiBell } from 'react-icons/hi';
import { useGeneral } from '../../../contexts';
import { useUnsavedChanges } from '../../../contexts/UnsavedChangesContext';
import { useEventDetails } from '../../../hooks/useEventDetails';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import transitionStyles from '../../../styles/transitions.module.css';
import Modal from '../../../components/ui/Modal/Modal';
import { Tabs, Tab } from '../../../components/ui/Tabs';
import GoogleMap from '../../../components/ui/GoogleMap/GoogleMap';
import GooglePlacesAutocomplete from '../../../components/ui/GooglePlacesAutocomplete/GooglePlacesAutocomplete';
import FormInput from '../../../components/ui/Form/FormInput';
import FormTextarea from '../../../components/ui/Form/FormTextarea';
import { RichTextEditor } from '../../../components/ui/RichTextEditor';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import TimePicker from '../../../components/ui/TimePicker/TimePicker';
import formStyles from '../../../components/ui/Form/Form.module.css';
import { CoverFile, EventDetails, ParticipationType } from '../../../types';
import { timestampToApiFormat, splitDateTime, combineDateTime } from '../../../utils/dateTimeFormatters';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import { createSlug, extractEventIdFromAdminUrl } from '../../../utils/urlSlug';
import { hasHtmlContent } from '../../../utils/htmlUtils';
import styles from './EventSettingsPage.module.css';

import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';

const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

const getParticipationTypeFromTickets = (
  tickets?: Array<{ type?: string }> | null
): ParticipationType => {
  if (!Array.isArray(tickets)) {
    return 'ticket';
  }

  const hasJoinTicket = tickets.some((ticket) => isLcvJoinTicketType(ticket.type));
  const hasCustomTicket = tickets.some((ticket) => {
    const ticketType = (ticket.type || '').trim().toLowerCase();
    return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
  });

  return hasJoinTicket && !hasCustomTicket ? 'rsvp' : 'ticket';
};

const parseBooleanFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
};

const EventSettingsPage: React.FC = () => {
  const { channel, event: basicEvent } = useOutletContext<OrganizerPanelOutletContext>();
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;
  const { setHasUnsavedChanges, pendingNavigation, clearPendingNavigation } = useUnsavedChanges();

  // Extract eventID from URL as fallback when basicEvent isn't available yet
  // This handles the case when navigating to a newly created event before the events list is refreshed
  const eventID = useMemo(() => {
    // Use basicEvent.eventID if available
    if (basicEvent?.eventID) {
      return basicEvent.eventID;
    }
    // Fallback: extract from URL directly
    const urlEventID = extractEventIdFromAdminUrl(location.pathname);
    return urlEventID || '';
  }, [basicEvent?.eventID, location.pathname]);

  // Fetch full event details
  const { event: eventDetails, loading: eventLoading, refreshEvent } = useEventDetails({
    token: token || null,
    eventID,
  });

  const [activeTab, setActiveTab] = useState<'details' | 'location' | 'danger'>('details');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingAnnouncement, setSendingAnnouncement] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  // Helper to get initial values from event
  const getInitialValues = (evt: EventDetails | null) => {
    if (!evt) {
      return {
        title: '',
        description: '',
        isOnline: false,
        website: '',
        locationAddress: '',
        locationLatitude: 0,
        locationLongitude: 0,
        locationAddressDefinition: '',
        city: '',
        organizedBy: '',
        contactEmail: '',
        contactPhone: '',
        ticketSaleStartDateTime: '',
        ticketSaleTime: '',
        hasCertificate: false,
        isPublic: "1" as "0" | "1" | "3",
        usersCanSendMessage: true,
        instagram: '',
        x: '',
        socialWebsite: '',
        eventStartDate: '',
        eventStartTime: '',
        eventEndDate: '',
        eventEndTime: '',
      };
    }

    const startDateTime = evt.eventStartDateTime ? timestampToApiFormat(evt.eventStartDateTime) : '';
    const endDateTime = evt.eventEndDateTime ? timestampToApiFormat(evt.eventEndDateTime) : '';
    const ticketSaleDateTime = evt.ticketSaleStartDateTime ? timestampToApiFormat(evt.ticketSaleStartDateTime) : '';

    const startSplit = startDateTime ? splitDateTime(startDateTime) : { date: '', time: '' };
    const endSplit = endDateTime ? splitDateTime(endDateTime) : { date: '', time: '' };
    const ticketSaleSplit = ticketSaleDateTime ? splitDateTime(ticketSaleDateTime) : { date: '', time: '' };

    return {
      title: evt.title || evt.eventTitle || '',
      description: evt.description || '',
      isOnline: evt.isOnline === "1",
      website: evt.website || '',
      locationAddress: evt.locationAddress || '',
      locationLatitude: Number(evt.locationLatitude),
      locationLongitude: Number(evt.locationLongitude),
      locationAddressDefinition: evt.locationAddressDefinition || '',
      city: evt.city || '',
      organizedBy: evt.organizedBy || '',
      contactEmail: evt.contactEmail || '',
      contactPhone: evt.contactPhone || '',
      ticketSaleStartDateTime: ticketSaleSplit.date,
      ticketSaleTime: ticketSaleSplit.time,
      hasCertificate: parseBooleanFlag(evt.hasCertificate),
      isPublic: (evt.isPublic || "1") as "0" | "1" | "3",
      usersCanSendMessage: evt.usersCanSendMessage !== false,
      instagram: evt.socialMediaLinks?.instagram || '',
      x: evt.socialMediaLinks?.x || '',
      socialWebsite: evt.socialMediaLinks?.website || '',
      eventStartDate: startSplit.date,
      eventStartTime: startSplit.time,
      eventEndDate: endSplit.date,
      eventEndTime: endSplit.time,
    };
  };

  // Initial values for comparison
  const initialValuesRef = useRef(getInitialValues(eventDetails));
  const eventIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);

  // Form state - initialize with event values
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [website, setWebsite] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationLatitude, setLocationLatitude] = useState<number>(0);
  const [locationLongitude, setLocationLongitude] = useState<number>(0);
  const [locationAddressDefinition, setLocationAddressDefinition] = useState('');
  const [city, setCity] = useState('');
  const [organizedBy, setOrganizedBy] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [ticketSaleStartDateTime, setTicketSaleStartDateTime] = useState('');
  const [ticketSaleTime, setTicketSaleTime] = useState('');
  const [hasCertificate, setHasCertificate] = useState(false);
  const [isPublic, setIsPublic] = useState<"0" | "1" | "3">("1");
  const [usersCanSendMessage, setUsersCanSendMessage] = useState(true);
  const [instagram, setInstagram] = useState('');
  const [x, setX] = useState('');
  const [socialWebsite, setSocialWebsite] = useState('');
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');

  // Image state
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<CoverFile | null>(null);
  const participationType = useMemo<ParticipationType>(
    () => getParticipationTypeFromTickets(eventDetails?.tickets),
    [eventDetails?.tickets]
  );

  // Update initial values and form state when event loads or changes
  useEffect(() => {
    if (eventDetails) {
      const currentEventId = eventDetails.eventID;
      const initial = getInitialValues(eventDetails);
      
      if (eventIdRef.current !== currentEventId || !isInitializedRef.current) {
        eventIdRef.current = currentEventId;
        isInitializedRef.current = true;
        initialValuesRef.current = initial;
        
        // Reset form state
        setTitle(initial.title);
        setDescription(initial.description);
        setIsOnline(initial.isOnline);
        setWebsite(initial.website);
        setLocationAddress(initial.locationAddress);
        setLocationLatitude(Number(initial.locationLatitude));
        setLocationLongitude(Number(initial.locationLongitude));
        setLocationAddressDefinition(initial.locationAddressDefinition);
        setCity(initial.city);
        setOrganizedBy(initial.organizedBy);
        setContactEmail(initial.contactEmail);
        setContactPhone(initial.contactPhone);
        setTicketSaleStartDateTime(initial.ticketSaleStartDateTime);
        setTicketSaleTime(initial.ticketSaleTime);
        setHasCertificate(initial.hasCertificate);
        setIsPublic(initial.isPublic);

        setUsersCanSendMessage(initial.usersCanSendMessage);
        setInstagram(initial.instagram);
        setX(initial.x);
        setSocialWebsite(initial.socialWebsite);
        setEventStartDate(initial.eventStartDate);
        setEventStartTime(initial.eventStartTime);
        setEventEndDate(initial.eventEndDate);
        setEventEndTime(initial.eventEndTime);
        
        // Set cover image
        const coverUrl = eventDetails.cover || eventDetails.image || eventDetails.bannerImage;
        setCoverImage(coverUrl || null);
        setCoverFile(null);
      } else if (eventIdRef.current === currentEventId && isInitializedRef.current && !saving) {
        // Update initial values when event refreshes after save
        // Also update form state to match the refreshed event (in case API returned different values)
        // This ensures hasUnsavedChanges becomes false after successful save
        initialValuesRef.current = initial;
        
        // Sync form state with refreshed event data
        setTitle(initial.title);
        setDescription(initial.description);
        setIsOnline(initial.isOnline);
        setWebsite(initial.website);
        setLocationAddress(initial.locationAddress);
        setLocationLatitude(Number(initial.locationLatitude));
        setLocationLongitude(Number(initial.locationLongitude));
        setLocationAddressDefinition(initial.locationAddressDefinition);
        setCity(initial.city);
        setOrganizedBy(initial.organizedBy);
        setContactEmail(initial.contactEmail);
        setContactPhone(initial.contactPhone);
        setTicketSaleStartDateTime(initial.ticketSaleStartDateTime);
        setTicketSaleTime(initial.ticketSaleTime);
        setHasCertificate(initial.hasCertificate);
        setIsPublic(initial.isPublic);

        setUsersCanSendMessage(initial.usersCanSendMessage);
        setInstagram(initial.instagram);
        setX(initial.x);
        setSocialWebsite(initial.socialWebsite);
        setEventStartDate(initial.eventStartDate);
        setEventStartTime(initial.eventStartTime);
        setEventEndDate(initial.eventEndDate);
        setEventEndTime(initial.eventEndTime);
        
        // Update cover image
        const coverUrl = eventDetails.cover || eventDetails.image || eventDetails.bannerImage;
        setCoverImage(coverUrl || null);
        setCoverFile(null);
      }
    }
  }, [eventDetails, saving]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!isInitializedRef.current) {
      return false;
    }
    const initial = initialValuesRef.current;
    
    // Compare all fields
    const hasChanges = (
      title !== initial.title ||
      description !== initial.description ||
      isOnline !== initial.isOnline ||
      website !== initial.website ||
      locationAddress !== initial.locationAddress ||
      locationLatitude !== initial.locationLatitude ||
      locationLongitude !== initial.locationLongitude ||
      locationAddressDefinition !== initial.locationAddressDefinition ||
      city !== initial.city ||
      organizedBy !== initial.organizedBy ||
      contactEmail !== initial.contactEmail ||
      contactPhone !== initial.contactPhone ||
      ticketSaleStartDateTime !== initial.ticketSaleStartDateTime ||
      ticketSaleTime !== initial.ticketSaleTime ||
      hasCertificate !== initial.hasCertificate ||
      isPublic !== initial.isPublic ||
      usersCanSendMessage !== initial.usersCanSendMessage ||
      instagram !== initial.instagram ||
      x !== initial.x ||
      socialWebsite !== initial.socialWebsite ||
      eventStartDate !== initial.eventStartDate ||
      eventStartTime !== initial.eventStartTime ||
      eventEndDate !== initial.eventEndDate ||
      eventEndTime !== initial.eventEndTime ||
      coverFile !== null
    );
    return hasChanges;
  }, [
    title, description, isOnline, website, locationAddress, locationLatitude, locationLongitude, locationAddressDefinition,
    city, organizedBy, contactEmail, contactPhone,
    ticketSaleStartDateTime, ticketSaleTime, hasCertificate, isPublic, usersCanSendMessage, instagram, x, socialWebsite,
    eventStartDate, eventStartTime, eventEndDate, eventEndTime, coverFile
  ]);

  // Update unsaved changes context
  useLayoutEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Show modal when navigation is blocked
  useEffect(() => {
    if (pendingNavigation && hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else if (!pendingNavigation) {
      setShowUnsavedModal(false);
    }
  }, [pendingNavigation, hasUnsavedChanges]);

  // Handle navigation away from page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleCoverUpload = async (file: File) => {
    try {
      const coverFileObj = await convertFileToBase64(file);
      setCoverFile(coverFileObj);
      const preview = URL.createObjectURL(file);
      setCoverImage(preview);
    } catch (error) {
      toastError(getTranslation('organizerPanel.imageUploadError') || 'Resim yüklenemedi');
    }
  };

  const handleSave = async () => {
    if (!token || !eventDetails?.eventID || !channel?.channelID) return;

    if (!hasUnsavedChanges) {
      toastSuccess(getTranslation('organizerPanel.noChangesMade') || 'Değişiklik yapılmadı');
      return;
    }

    // Validation
    if (!title.trim()) {
      toastError(getTranslation('organizerPanel.titleRequired') || 'Etkinlik başlığı gereklidir');
      return;
    }
    if (!hasHtmlContent(description)) {
      toastError(getTranslation('organizerPanel.descriptionRequired') || 'Etkinlik açıklaması gereklidir');
      return;
    }
    if (!eventStartDate || !eventStartTime) {
      toastError(getTranslation('organizerPanel.startDateTimeRequired') || 'Başlangıç tarihi ve saati gereklidir');
      return;
    }
    if (!eventEndDate || !eventEndTime) {
      toastError(getTranslation('organizerPanel.endDateTimeRequired') || 'Bitiş tarihi ve saati gereklidir');
      return;
    }
    if (!isOnline && (!locationAddress || !locationLatitude || !locationLongitude)) {
      toastError(getTranslation('organizerPanel.locationRequired') || 'Konum bilgileri gereklidir');
      return;
    }
    if (isOnline && !website.trim()) {
      toastError(getTranslation('organizerPanel.websiteRequired') || 'Online etkinlikler için website gereklidir');
      return;
    }
    setSaving(true);
    try {
      const eventStartDateTime = combineDateTime(eventStartDate, eventStartTime);
      const eventEndDateTime = combineDateTime(eventEndDate, eventEndTime);
      const ticketSaleStart = ticketSaleStartDateTime && ticketSaleTime
        ? combineDateTime(ticketSaleStartDateTime, ticketSaleTime)
        : null;

      const socialMediaLinks = {
        website: "string", // mock value
        instagram: "string", // mock value
        x: "string", // mock value
      };

      const hasTicketsValue = true;

      // Calculate isFree based on existing tickets (excluding sponsor)
      // 1 = free, 2 = paid, 3 = mixed
      const existingTickets = Array.isArray(eventDetails.tickets) ? eventDetails.tickets : [];
      const customExisting = existingTickets.filter((ticket) => {
        const type = (ticket.type || '').trim().toLowerCase();
        return type !== 'sponsor' && !isLcvJoinTicketType(type);
      });
      let calculatedIsFree = 1; // Default to free
      if (customExisting.length > 0) {
        const hasFree = customExisting.some(t => Number(t.price) === 0);
        const hasPaid = customExisting.some(t => Number(t.price) > 0);
        if (hasFree && hasPaid) {
          calculatedIsFree = 3;
        } else if (hasPaid) {
          calculatedIsFree = 2;
        }
      }

      const payload: any = {
        eventID: eventDetails.eventID,
        channelID: channel.channelID,
        title: title.trim(),
        description: description.trim(),
        eventStartDateTime,
        eventEndDateTime,
        isOnline: isOnline,
        website: isOnline ? website.trim() : "",
        locationAddress: !isOnline ? locationAddress.trim() : "",
        locationLatitude: !isOnline ? locationLatitude : "",
        locationLongitude: !isOnline ? locationLongitude : "",
        locationAddressDefinition: !isOnline ? locationAddressDefinition.trim() : "",
        city: !isOnline ? city.trim() : null,
        organizedBy: "string", // mock value
        hasTickets: hasTicketsValue,
        isFree: calculatedIsFree,
        ticketSaleStartDateTime: hasTicketsValue ? ticketSaleStart : null,
        contactEmail: "string", // mock value
        ticketSaleNotificationEmail: hasTicketsValue ? "string" : "", // mock value
        customerNotificationMailTemplate: '',
        contactPhone: "string", // mock value
        socialMediaLinks,
        usersCanSendMessage: true, // always true
        hasCertificate,
        isPublic: Number(isPublic),
        status: 1,
      };

      // Always include cover (use existing if no new file, or new file)
      if (coverFile) {
        payload.cover = coverFile;
      } else if (eventDetails.cover) {
        // If cover is a URL, we need to fetch and convert it, or the API might handle it
        // For now, we'll send the existing cover as-is if it's already a CoverFile format
        // Otherwise, we might need to send it as a string URL
        // The API should handle this case
      }

      await Services.SocialHiveUpdateEvent(token, payload);

      toastSuccess(getTranslation('organizerPanel.eventSettingsSaved') || 'Etkinlik ayarları kaydedildi');

      // Reset file state and refresh event
      setCoverFile(null);
      refreshEvent();
      
      // Note: initialValuesRef will be updated when eventDetails refreshes via useEffect
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message || 
        getTranslation('organizerPanel.eventSettingsSaveError') || 'Etkinlik ayarları kaydedilemedi';
      toastError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleUnsavedModalClose = () => {
    setShowUnsavedModal(false);
    clearPendingNavigation();
  };

  const handleUnsavedModalDiscard = () => {
    // Reset form to initial values
    if (eventDetails && initialValuesRef.current) {
      const initial = initialValuesRef.current;
      
      setTitle(initial.title);
      setDescription(initial.description);
      setIsOnline(initial.isOnline);
      setWebsite(initial.website);
      setLocationAddress(initial.locationAddress);
      setLocationLatitude(Number(initial.locationLatitude));
      setLocationLongitude(Number(initial.locationLongitude));
      setLocationAddressDefinition(initial.locationAddressDefinition);
      setCity(initial.city);
      setOrganizedBy(initial.organizedBy);
      setContactEmail(initial.contactEmail);
      setContactPhone(initial.contactPhone);
      setTicketSaleStartDateTime(initial.ticketSaleStartDateTime);
      setTicketSaleTime(initial.ticketSaleTime);
      setHasCertificate(initial.hasCertificate);
      setIsPublic(initial.isPublic);
      setUsersCanSendMessage(initial.usersCanSendMessage);
      setInstagram(initial.instagram);
      setX(initial.x);
      setSocialWebsite(initial.socialWebsite);
      setEventStartDate(initial.eventStartDate);
      setEventStartTime(initial.eventStartTime);
      setEventEndDate(initial.eventEndDate);
      setEventEndTime(initial.eventEndTime);
      setCoverFile(null);
    }

    setShowUnsavedModal(false);

    // Navigate to intended page
    if (pendingNavigation) {
      pendingNavigation();
    }
    clearPendingNavigation();
  };

  const handleUnsavedModalSave = async () => {
    await handleSave();
    setShowUnsavedModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
    clearPendingNavigation();
  };

  const handleDeleteEvent = async () => {
    if (!token || !eventDetails?.eventID) return;

    setDeleting(true);
    try {
      await Services.SocialHiveRemoveEvent(token, eventDetails.eventID);
      toastSuccess(getTranslation('organizerPanel.eventDeleted') || 'Etkinlik başarıyla silindi');
      
      // Navigate back to events list
      const channelTitle = channel?.channelTitle || channel?.title;
      if (channelTitle) {
        const channelSlug = createSlug(channelTitle);
        navigate(`/communities/${channelSlug}/admin`, { replace: true });
      }
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error || 
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.eventDeleteError') || 'Etkinlik silinemedi';
      toastError(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Check if announcement email has already been sent
  const isAnnouncementSent = eventDetails?.isSentFirstIntroductionEmailToUsers === "1";

  const handleSendAnnouncement = async () => {
    if (!token || !eventDetails?.eventID) return;

    setSendingAnnouncement(true);
    try {
      const response = await Services.SocialHiveSendEventFirstIntroductionEmail(token, eventDetails.eventID);
      if (response.data.status) {
        toastSuccess(getTranslation('organizerPanel.announcementSent') || 'Duyuru e-postası başarıyla gönderildi');
        // Refresh event details to update the button state
        refreshEvent();
      } else {
        toastError(response.data.message || getTranslation('organizerPanel.announcementError') || 'Duyuru e-postası gönderilemedi');
      }
    } catch (error: unknown) {
      const errorMessage = (error as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error ||
        (error as { message?: string })?.message ||
        getTranslation('organizerPanel.announcementError') || 'Duyuru e-postası gönderilemedi';
      toastError(errorMessage);
    } finally {
      setSendingAnnouncement(false);
      setShowAnnouncementModal(false);
    }
  };

  const lat = locationLatitude && !isNaN(locationLatitude) ? locationLatitude : 0;
  const lng = locationLongitude && !isNaN(locationLongitude) ? locationLongitude : 0;
  const showMap = !isOnline && lat !== 0 && lng !== 0;

  return (
    <div className={styles.eventSettingsPage}>
      {(eventLoading || !eventDetails) ? (
        <>
          <div className={styles.header}>
            <LoadingSkeleton height={32} width="200px" />
            <LoadingSkeleton height={40} width="100px" />
          </div>
          <div className={styles.content}>
            <div className={styles.tabContent}>
              <div style={{ marginBottom: '24px' }}>
                <LoadingSkeleton height={300} width="100%" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <LoadingSkeleton height={40} width="100%" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <LoadingSkeleton height={40} width="100%" />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <LoadingSkeleton height={40} width="100%" />
              </div>
              <LoadingSkeleton height={100} width="100%" />
            </div>
          </div>
        </>
      ) : (
        <>
      <Modal
        isOpen={showUnsavedModal}
        onClose={handleUnsavedModalClose}
        title={getTranslation('organizerPanel.unsavedChanges') || 'Kaydedilmemiş Değişiklikler'}
        variant="confirm"
        message={getTranslation('organizerPanel.unsavedChangesMessage') || 'Kaydedilmemiş değişiklikleriniz var. Devam etmek istediğinizden emin misiniz?'}
        confirmText={getTranslation('organizerPanel.saveAndContinue') || 'Kaydet ve Devam Et'}
        cancelText={getTranslation('organizerPanel.discardChanges') || 'Vazgeç'}
        onConfirm={handleUnsavedModalSave}
        onCancel={handleUnsavedModalDiscard}
        confirmVariant="primary"
      />

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={getTranslation('organizerPanel.deleteEvent') || 'Etkinliği Sil'}
        variant="confirm"
        message={getTranslation('organizerPanel.deleteEventMessage') || 'Bu etkinliği silmek istediğinizden emin misiniz? Bu işlem geri alınamaz. Tüm biletler, konuşmacılar, sponsorlar ve katılımcı bilgileri kalıcı olarak silinecektir.'}
        confirmText={getTranslation('organizerPanel.delete') || 'Sil'}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleDeleteEvent}
        onCancel={() => setShowDeleteModal(false)}
        isDanger={true}
      />

      <Modal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        title={getTranslation('organizerPanel.sendAnnouncement') || 'Duyuru Gönder'}
        variant="confirm"
        message={getTranslation('organizerPanel.sendAnnouncementMessage') || 'Tüm etkinlik katılımcılarına tanıtım e-postası göndermek istediğinizden emin misiniz? Bu işlem yalnızca bir kez yapılabilir.'}
        confirmText={sendingAnnouncement ? (getTranslation('common.sending') || 'Gönderiliyor...') : (getTranslation('organizerPanel.sendEmail') || 'E-posta Gönder')}
        cancelText={getTranslation('common.cancel') || 'İptal'}
        onConfirm={handleSendAnnouncement}
        onCancel={() => setShowAnnouncementModal(false)}
        confirmVariant="primary"
      />

      <ImageUploadModal
        isOpen={showCoverModal}
        onClose={() => setShowCoverModal(false)}
        onUpload={handleCoverUpload}
        title={getTranslation('organizerPanel.uploadCoverImage') || 'Kapak Görseli Yükle'}
        aspectRatio="16:9"
        recommendedSize="1920x1080"
        getTranslation={getTranslation}
      />

      <div className={styles.header}>
        <Tabs variant="default" className={styles.tabs}>
          <Tab
            variant="default"
            isActive={activeTab === 'details'}
            onClick={() => setActiveTab('details')}
          >
            {getTranslation('organizerPanel.eventDetails') || 'Etkinlik Detayları'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'location'}
            onClick={() => setActiveTab('location')}
          >
            {getTranslation('organizerPanel.locationDetails') || 'Konum Detayları'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'danger'}
            onClick={() => setActiveTab('danger')}
            danger={true}
          >
            {getTranslation('organizerPanel.dangerZone') || 'Tehlikeli Bölge'}
          </Tab>
        </Tabs>
        <div className={styles.headerActions}>
          <button
            type="button"
            className={`${styles.announcementButton} ${isAnnouncementSent ? styles.announcementButtonSent : ''}`}
            onClick={() => !isAnnouncementSent && setShowAnnouncementModal(true)}
            disabled={isAnnouncementSent || sendingAnnouncement}
            title={isAnnouncementSent
              ? (getTranslation('organizerPanel.announcementAlreadySent') || 'Duyuru zaten gönderildi')
              : (getTranslation('organizerPanel.sendAnnouncement') || 'Duyuru Gönder')
            }
            aria-label={isAnnouncementSent
              ? (getTranslation('organizerPanel.announcementAlreadySent') || 'Duyuru zaten gönderildi')
              : (getTranslation('organizerPanel.sendAnnouncement') || 'Duyuru Gönder')
            }
          >
            {/* @ts-ignore */}
            <HiBell size={20} />
          </button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleSave}
            disabled={saving}
            className={styles.saveButton}
          >
            {saving
              ? (getTranslation('common.saving') || 'Kaydediliyor...')
              : (getTranslation('organizerPanel.saveChanges') || 'Değişiklikleri Kaydet')
            }
          </Button>
        </div>
      </div>

      <div className={styles.content} key={activeTab}>
        <div className={transitionStyles.fadeIn}>
        {activeTab === 'details' && (
          <div className={styles.tabContent}>
          {/* Cover Image Upload */}
          <div className={styles.coverUploadSection}>
            <label className={styles.label}>
              {getTranslation('organizerPanel.coverImage') || 'Kapak Görseli'} *
            </label>
            <div
              className={styles.coverPlaceholder}
              onClick={() => setShowCoverModal(true)}
            >
              {coverImage ? (
                <img src={coverImage} alt={getTranslation('organizerPanel.coverImage') || 'Cover'} className={styles.coverImage} />
              ) : (
                <div className={styles.uploadIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className={formStyles.formFields}>
              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.eventTitle') || 'Etkinlik Başlığı'} *
                </label>
                <FormInput
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={getTranslation('organizerPanel.eventTitlePlaceholder') || 'Etkinlik başlığını girin'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.eventDescription') || 'Etkinlik Açıklaması'} *
                </label>
                <RichTextEditor
                  value={description}
                  onChange={(html) => setDescription(html)}
                  placeholder={getTranslation('organizerPanel.eventDescriptionPlaceholder') || 'Etkinlik açıklamasını girin'}
                />
              </div>

              <div className={formStyles.formField}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    checked={isOnline}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsOnline(checked);
                      if (checked) {
                        setLocationLatitude(0);
                        setLocationLongitude(0);
                        setLocationAddress('');
                      }
                    }}
                    className={styles.checkbox}
                  />
                  <span>{getTranslation('organizerPanel.onlineEvent') || 'Online Etkinlik'}</span>
                </label>
              </div>

              {isOnline ? (
                <div className={formStyles.formField}>
                  <label className={styles.label}>
                    {getTranslation('organizerPanel.eventLink') || 'Etkinlik Linki'} *
                  </label>
                  <FormInput
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder={getTranslation('organizerPanel.eventLinkPlaceholder') || 'https://...'}
                  />
                </div>
              ) : null}

              <div className={formStyles.formRow}>
                <div className={formStyles.formField}>
                  <DatePicker
                    value={eventStartDate}
                    onChange={(value) => setEventStartDate(value)}
                    label={getTranslation('organizerPanel.startDate') || 'Başlangıç Tarihi'}
                    locale={language.language}
                  />
                </div>
                <div className={formStyles.formField}>
                  <TimePicker
                    value={eventStartTime}
                    onChange={(value) => setEventStartTime(value)}
                    label={getTranslation('organizerPanel.startTime') || 'Başlangıç Saati'}
                    locale={language.language}
                  />
                </div>
              </div>

              <div className={formStyles.formRow}>
                <div className={formStyles.formField}>
                  <DatePicker
                    value={eventEndDate}
                    onChange={(value) => setEventEndDate(value)}
                    label={getTranslation('organizerPanel.endDate') || 'Bitiş Tarihi'}
                    minDate={eventStartDate || undefined}
                    locale={language.language}
                  />
                </div>
                <div className={formStyles.formField}>
                  <TimePicker
                    value={eventEndTime}
                    onChange={(value) => setEventEndTime(value)}
                    label={getTranslation('organizerPanel.endTime') || 'Bitiş Saati'}
                    locale={language.language}
                  />
                </div>
              </div>


              {/* Ticket Sale Date/Time */}
              <div className={formStyles.formRow}>
                <div className={formStyles.formField}>
                  <DatePicker
                    value={ticketSaleStartDateTime}
                    onChange={(value) => setTicketSaleStartDateTime(value)}
                    label={getTranslation('organizerPanel.ticketSaleStartDate') || 'Bilet Satış Başlangıç Tarihi'}
                    locale={language.language}
                  />
                </div>
                <div className={formStyles.formField}>
                  <TimePicker
                    value={ticketSaleTime}
                    onChange={(value) => setTicketSaleTime(value)}
                    label={getTranslation('organizerPanel.ticketSaleStartTime') || 'Bilet Satış Başlangıç Saati'}
                    locale={language.language}
                  />
                </div>
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.participationType') || 'Katılım Tipi'}
                </label>
                <div className={formStyles.radioGroup}>
                  <label className={`${formStyles.radioLabel} ${styles.radioDisabled}`}>
                    <input
                      type="radio"
                      name="participationType"
                      className={formStyles.radio}
                      checked={participationType === 'ticket'}
                      disabled
                      readOnly
                    />
                    <span>{getTranslation('organizerPanel.participationTypeTicket') || 'Bilet'}</span>
                  </label>
                  <label className={`${formStyles.radioLabel} ${styles.radioDisabled}`}>
                    <input
                      type="radio"
                      name="participationType"
                      className={formStyles.radio}
                      checked={participationType === 'rsvp'}
                      disabled
                      readOnly
                    />
                    <span>{getTranslation('organizerPanel.participationTypeRsvp') || 'LCV'}</span>
                  </label>
                </div>
                <p className={styles.participationWarning}>
                  {getTranslation('organizerPanel.participationTypeLocked') || 'Katılım tipi etkinlik oluşturulduktan sonra değiştirilemez.'}
                </p>
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.hasCertificateQuestion') || 'Bu etkinliğin sonunda katılımcılar sertifika alabilir mi?'}
                </label>
                <div className={formStyles.radioGroup}>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="hasCertificate"
                      className={formStyles.radio}
                      checked={hasCertificate === true}
                      onChange={() => setHasCertificate(true)}
                    />
                    <span>{getTranslation('common.yes') || 'Evet'}</span>
                  </label>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="hasCertificate"
                      className={formStyles.radio}
                      checked={hasCertificate === false}
                      onChange={() => setHasCertificate(false)}
                    />
                    <span>{getTranslation('common.no') || 'Hayır'}</span>
                  </label>
                </div>
              </div>

              <div className={formStyles.formField}>
                <label className={styles.label}>
                  {getTranslation('organizerPanel.eventVisibility') || 'Event Visibility'}
                </label>
                <select
                  value={isPublic}
                  onChange={(e) => setIsPublic(e.target.value as "0" | "1" | "3")}
                  className={formStyles.select}
                >
                  <option value="1">{getTranslation('organizerPanel.eventVisibilityPublic') || 'Public - Anyone can discover this event'}</option>
                  <option value="0">{getTranslation('organizerPanel.eventVisibilityPrivate') || 'Private - Only community members can see'}</option>
                  <option value="3">{getTranslation('organizerPanel.eventVisibilityUnlisted') || 'Unlisted - Only people with the link can access'}</option>
                </select>
              </div>


            </div>
          </div>
        )}
        {activeTab === 'location' && (
          <div className={styles.tabContent}>
            {!isOnline ? (
              <>
                <div className={formStyles.formFields}>
                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.locationAddress') || 'Konum Adresi'} *
                    </label>
                    <GooglePlacesAutocomplete
                      value={locationAddress}
                      onChange={(address, fullAddress, lat, lng, extractedCity) => {
                        // Update locationAddress when user types manually
                        setLocationAddress(address);
                        // Only update coordinates and full address if a place was selected (lat/lng are valid)
                        if (lat !== 0 && lng !== 0) {
                          setLocationAddressDefinition(fullAddress);
                          setLocationLatitude(lat);
                          setLocationLongitude(lng);
                          if (extractedCity) {
                            setCity(extractedCity);
                          }
                        }
                      }}
                      placeholder={getTranslation('organizerPanel.locationAddressPlaceholder') || 'Adres arayın veya haritadan seçin...'}
                    />
                  </div>

                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.fullAddress') || 'Tam Adres'}
                    </label>
                    <FormTextarea
                      value={locationAddressDefinition}
                      onChange={(e) => setLocationAddressDefinition(e.target.value)}
                      rows={3}
                      placeholder={getTranslation('organizerPanel.fullAddressPlaceholder') || 'Tam adres bilgisi otomatik olarak doldurulacaktır'}
                    />
                  </div>

                  <div className={formStyles.formField}>
                    <label className={styles.label}>
                      {getTranslation('organizerPanel.city') || 'Şehir'}
                    </label>
                    <FormInput
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={getTranslation('organizerPanel.cityPlaceholder') || 'Şehir adı girin'}
                    />
                  </div>

                  <div className={formStyles.formRow}>
                    <div className={formStyles.formField}>
                      <label className={styles.label}>
                        {getTranslation('organizerPanel.latitude') || 'Enlem'} *
                      </label>
                      <FormInput
                        type="number"
                        step="any"
                        value={locationLatitude && !isNaN(locationLatitude) ? locationLatitude : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocationLatitude(value === '' ? 0 : Number(value));
                        }}
                        placeholder={getTranslation('organizerPanel.latitudePlaceholder') || '41.0082'}
                      />
                    </div>
                    <div className={formStyles.formField}>
                      <label className={styles.label}>
                        {getTranslation('organizerPanel.longitude') || 'Boylam'} *
                      </label>
                      <FormInput
                        type="number"
                        step="any"
                        value={locationLongitude && !isNaN(locationLongitude) ? locationLongitude : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocationLongitude(value === '' ? 0 : Number(value));
                        }}
                        placeholder={getTranslation('organizerPanel.longitudePlaceholder') || '28.9784'}
                      />
                    </div>
                  </div>

                  {showMap ? (
                    <div className={styles.mapContainer}>
                      <GoogleMap
                        latitude={lat}
                        longitude={lng}
                        address={locationAddress}
                        interactive={true}
                        onLocationSelect={(lat, lng) => {
                          setLocationLatitude(lat);
                          setLocationLongitude(lng);
                          // Reverse geocode to get address - only update if locationAddress is empty
                          if (window.google && window.google.maps) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
                              if (status === 'OK' && results && results[0]) {
                                // Only update locationAddress if it's empty, otherwise keep user's custom name
                                if (!locationAddress.trim()) {
                                  setLocationAddress(results[0].name || results[0].formatted_address);
                                }
                                setLocationAddressDefinition(results[0].formatted_address);
                                // Extract city from address_components
                                const addressComponents = results[0].address_components;
                                if (addressComponents) {
                                  let locality, adminLevel2, adminLevel1;
                                  for (const component of addressComponents) {
                                    const types = component.types || [];
                                    if (types.includes('locality')) locality = component.long_name;
                                    if (types.includes('administrative_area_level_2')) adminLevel2 = component.long_name;
                                    if (types.includes('administrative_area_level_1')) adminLevel1 = component.long_name;
                                  }
                                  // For Turkish addresses, combine district and province
                                  if (adminLevel2 && adminLevel1 && !locality) {
                                    setCity(`${adminLevel2} / ${adminLevel1}`);
                                  } else if (locality) {
                                    setCity(adminLevel1 && adminLevel1 !== locality ? `${locality}, ${adminLevel1}` : locality);
                                  } else if (adminLevel2 || adminLevel1) {
                                    setCity(adminLevel2 || adminLevel1);
                                  }
                                }
                              }
                            });
                          }
                        }}
                      />
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className={styles.onlineEventMessage}>
                <p>{getTranslation('organizerPanel.onlineEventNoAddress') || 'Bu bir online etkinliktir, adres bilgisi gerekmemektedir.'}</p>
              </div>
            )}
          </div>
        )}
        {activeTab === 'danger' && (
          <div className={styles.tabContent}>
            <div className={styles.dangerZone}>
              <h3 className={styles.dangerZoneTitle}>
                {getTranslation('organizerPanel.dangerZone') || 'Tehlikeli Bölge'}
              </h3>
              <p className={styles.dangerZoneDescription}>
                {getTranslation('organizerPanel.dangerZoneDescription') || 'Bu bölgedeki işlemler geri alınamaz. Lütfen dikkatli olun.'}
              </p>

              <div className={styles.dangerZoneItem}>
                <div className={styles.dangerZoneItemContent}>
                  <h4 className={styles.dangerZoneItemTitle}>
                    {getTranslation('organizerPanel.deleteEvent') || 'Etkinliği Sil'}
                  </h4>
                  <p className={styles.dangerZoneItemDescription}>
                    {getTranslation('organizerPanel.deleteEventDescription') || 'Bu etkinliği kalıcı olarak silin. Bu işlem geri alınamaz ve tüm veriler kaybolacaktır.'}
                  </p>
                </div>
                <Button
                  variant="orange"
                  size="medium"
                  onClick={() => setShowDeleteModal(true)}
                  disabled={deleting}
                  className={styles.deleteButton}
                >
                  {deleting
                    ? (getTranslation('common.deleting') || 'Siliniyor...')
                    : (getTranslation('organizerPanel.deleteEvent') || 'Etkinliği Sil')
                  }
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default EventSettingsPage;
